<script setup lang="ts">
import { computed, ref, watch  } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, maxLength, numeric } from '@vuelidate/validators'
import FileUpload from '../../Shared/FileUpload.vue'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import SsnInput from '../../Shared/SSNInput.vue'

const { t } = useI18n()
const props = defineProps<{ 
  disable?: boolean
  runBankCheck?: (u: any) => Promise<{ valid: boolean; errorMessage?: string }>
}>()

const { umsokn, onLoaded } = useUmsokn()

const localBankErrors = ref<Record<number, string>>({})
const showBankErrors = ref<Record<number, boolean>>({})

const rules = computed(() => {
  const list = umsokn.value?.childInformations ?? []
  const childRules: Record<number, any> = {}
  list.forEach((_:any, i:number) => {
    childRules[i] = {
      name: { required },
      ssn: { required },
      bankNumber: { required, minLength: minLength(4), maxLength: maxLength(4), numeric },
      ledger: { required, numeric },
      accountNumber: { required, numeric },
      childYounger: { required }
    }
  })
  return { umsokn: { childInformations: childRules } }
})

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  async validate() {
    // Reset all local bank errors
    localBankErrors.value = {}
    showBankErrors.value = {}
    
    const ok = await v$.value.$validate()
    if (!ok) return false

    // Run bank check for each child if runBankCheck is provided
    if (props.runBankCheck && umsokn.value?.childInformations) {
      for (let i = 0; i < umsokn.value.childInformations.length; i++) {
        const child = umsokn.value.childInformations[i]
        const res = await props.runBankCheck({
          bankNumber: child.bankNumber,
          ledger: child.ledger,
          accountNumber: child.accountNumber
        })
        if (!res.valid) {
          localBankErrors.value[i] = res.errorMessage || t('umsokn_bankareikningur_villa')
          showBankErrors.value[i] = true
          return false
        }
      }
    }
    return true
  }
})

onLoaded(() => {
  if (!Array.isArray(umsokn.value.childInformations)) umsokn.value.childInformations = []
  if (umsokn.value.childInformations.length === 0) {
    umsokn.value.childInformations.push({
      name: '',
      ssn: '',
      bankNumber: '',
      ledger: '',
      accountNumber: '',
      childYounger: null
    })
  }
})

function addChild() {
  umsokn.value.childInformations.push({
    name: '',
    ssn: '',
    bankNumber: '',
    ledger: '',
    accountNumber: '',
    childYounger: null
  })
}

function removeChild(index: number) {
  umsokn.value.childInformations.splice(index, 1)
  if (umsokn.value.childInformations.length === 0) addChild()
}

</script>

<template>
  <div v-if="umsokn">
    <v-row>
      <v-col>
        <h2 class="form__header">{{ $t('barnalifeyrir_umsokn_step3_header') }}</h2>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <p>{{ $t('barnalifeyrir_umsokn_step3_subtext') }}</p>
      </v-col>
    </v-row>

    <transition-group name="list">
      <div v-for="(child, index) in umsokn.childInformations" :key="'childInformations_' + index">
        <div v-if="index > 0" class="forms__divider__container">
          <div class="forms__divider"></div>
          <button
            v-if="!props.disable"
            type="button"
            class="forms__warning__list__wrapper"
            @click="removeChild(index)"
          >
            <img class="forms__warning__icon" src="@/assets/icons/Close.svg" alt="Remove" />
          </button>
        </div>

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="child.name"
              :readonly="props.disable"
              :label="$t('barnalifeyrir_umsokn_step3_name')"
              variant="outlined"
              density="comfortable"
              :error="v$.umsokn.childInformations[index]?.name?.$dirty && v$.umsokn.childInformations[index]?.name?.$error"
              :error-messages="
                v$.umsokn.childInformations[index]?.name?.$dirty &&
                v$.umsokn.childInformations[index]?.name?.required?.$invalid
                  ? [$t('barnalifeyrir_umsokn_step3_name_error')]
                  : []
              "
            />
          </v-col>

          <v-col cols="12" md="6">
            <SsnInput
              v-model="child.ssn"
              :readonly="props.disable"
              placeholder="######-####"
              variant="outlined"
              density="comfortable"
              :label="$t('barnalifeyrir_umsokn_step3_ssn')"
              :error="v$.umsokn.childInformations[index]?.ssn?.$dirty && v$.umsokn.childInformations[index]?.ssn?.$error"
              :error-messages="
                v$.umsokn.childInformations[index]?.ssn?.$dirty
                  ? [
                      v$.umsokn.childInformations[index]?.ssn?.required?.$invalid
                        ? $t('barnalifeyrir_umsokn_step3_ssn_error')
                        : '',
                    ].filter(Boolean)
                  : []
              "
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="child.bankNumber"
              :readonly="props.disable"
              :label="$t('umsokn_bankareikningur_banki')"
              variant="outlined"
              density="comfortable"
              :error="v$.umsokn.childInformations[index]?.bankNumber?.$dirty && v$.umsokn.childInformations[index]?.bankNumber?.$error"
              :error-messages="
                v$.umsokn.childInformations[index]?.bankNumber?.$dirty
                  ? [
                      v$.umsokn.childInformations[index]?.bankNumber?.required?.$invalid
                        ? $t('umsokn_bankareikningur_banki_error')
                        : '',
                      v$.umsokn.childInformations[index]?.bankNumber?.minLength?.$invalid
                        ? $t('umsokn_bankareikningur_banki_error_length')
                        : '',
                      v$.umsokn.childInformations[index]?.bankNumber?.maxLength?.$invalid
                        ? $t('umsokn_bankareikningur_banki_error_length')
                        : '',
                      v$.umsokn.childInformations[index]?.bankNumber?.numeric?.$invalid
                        ? $t('umsokn_bankareikningur_banki_error_numeric')
                        : ''
                    ].filter(Boolean)
                  : []
              "
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model="child.ledger"
              :readonly="props.disable"
              variant="outlined"
              density="comfortable"
              :label="$t('umsokn_bankareikningur_hofudbok')"
              :error="v$.umsokn.childInformations[index]?.ledger?.$dirty && v$.umsokn.childInformations[index]?.ledger?.$error"
              :error-messages="
                v$.umsokn.childInformations[index]?.ledger?.$dirty
                  ? [
                      v$.umsokn.childInformations[index]?.ledger?.required?.$invalid
                        ? $t('umsokn_bankareikningur_hofudbok_error')
                        : '',
                      v$.umsokn.childInformations[index]?.ledger?.numeric?.$invalid
                        ? $t('umsokn_bankareikningur_hofudbok_error_numeric')
                        : ''
                    ].filter(Boolean)
                  : []
              "
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model="child.accountNumber"
              :readonly="props.disable"
              variant="outlined"
              density="comfortable"
              :label="$t('umsokn_bankareikningur_reikningsnumer')"
              :error="v$.umsokn.childInformations[index]?.accountNumber?.$dirty && v$.umsokn.childInformations[index]?.accountNumber?.$error"
              :error-messages="
                v$.umsokn.childInformations[index]?.accountNumber?.$dirty
                  ? [
                      v$.umsokn.childInformations[index]?.accountNumber?.required?.$invalid
                        ? $t('umsokn_bankareikningur_reikningsnumer_error')
                        : '',
                      v$.umsokn.childInformations[index]?.accountNumber?.numeric?.$invalid
                        ? $t('umsokn_bankareikningur_reikningsnumer_error_numeric')
                        : ''
                    ].filter(Boolean)
                  : []
              "
            />
          </v-col>
        </v-row>

        <!-- Bank validation error for this child -->
        <v-row v-if="showBankErrors[index]">
          <v-col>
            <v-alert type="error" color="error" variant="flat" density="compact" class="mt-2" closable @click:close="showBankErrors[index] = false">
              {{ localBankErrors[index] }}
            </v-alert>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <p>{{ $t('barnalifeyrir_umsokn_step3_childbank') }}</p>
            <p style="font-weight: bold">{{ $t('barnalifeyrir_umsokn_step3_greiddur_eftir') }}</p>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-radio-group
              v-model="child.childYounger"
              :readonly="props.disable"
              :inline="true"
              hide-details
              variant="outlined"
              density="comfortable"
              :error="v$.umsokn.childInformations[index]?.childYounger?.$dirty && v$.umsokn.childInformations[index]?.childYounger?.$error"
            >
              <v-radio :label="$t('barnalifeyrir_umsokn_step3_stadgreidsla_yngri')" :value="1" />
              <v-radio :label="$t('barnalifeyrir_umsokn_step3_stadgreidsla_eldri')" :value="2" />
            </v-radio-group>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <p>{{ $t('barnalifeyrir_umsokn_step3_stadfesting') }}</p>
            <FileUpload
              :umsoknType="12"
              :umsoknId="umsokn.id"
              variant="outlined"
              density="comfortable"
              :filespace="index"
              :labelText="$t('barnalifeyrir_umsokn_step3_stadfesting_allowed')"
              :disable="props.disable"
            />
          </v-col>
        </v-row>
      </div>
    </transition-group>

    <div class="m-t-40" v-if="!props.disable">
      <v-btn
        color="primary"
        variant="outlined"
        prepend-icon="mdi-plus"
        class="mt-4"
        @click="addChild"
      >
        {{ $t('barnalifeyrir_umsokn_step3_add') }}
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
input { width: 100%; }
.forms .forms__warning__list__wrapper .forms__warning__close {
  position: absolute; top: 9px; right: 9px; width: 11.88px; height: 14.24px; z-index: 2;
}
</style>
